import { Burden, lazy, VoidCallback } from '@deqo/foundation';
import { Component, ObjectManager } from '../../../concepts/objects';
import { EditorSession } from '../../canvas/EditorSession';
import { MyProfileInfo } from '../../canvas/MyProfileInfo';
import { WidgetManager } from '../../canvas/workspace/widgets/WidgetManager';
import { ActiveObject, resolve } from '../../Container';
import { HandInfo } from '../hand/HandInfo';
import { IndexCard } from '../paper/IndexCard';
import { Sticky } from '../paper/Sticky';
import { Concealable, IsActuallyConcealed, IsConcealedHintVisible } from './Concealable';
import { ConcealmentIndex } from './ConcealmentIndex';
import { ConcealmentInfo } from './ConcealmentInfo';

export class ConcealMechanics extends ActiveObject {

    @resolve(ConcealmentIndex) index!: ConcealmentIndex;
    @resolve(EditorSession) session!: EditorSession;
    @resolve(ObjectManager) objects!: ObjectManager;
    @resolve(WidgetManager) widgets!: WidgetManager;

    private loaded = false;
    private unload = null as VoidCallback | null;

    @lazy()
    private get myUserId() {
        return this.session.get(MyProfileInfo).id;
    }

    public start(): void {
        this.onInfoChange();
        this.burden(
            this.session.observe(ConcealmentInfo, this.onInfoChange),
            this.session.observe(HandInfo, this.onInfoChange), // TODO: Remove eventually
            () => this.unload?.call(this),
        );
    }

    private load() {
        this.updateModeIfRequired();
        this.updateAttributes();
        return [
            // this.objects.on('updated', elmts => this.updateAttributesFor(elmts)),
            this.objects.createTrigger('Concealment.OnIndexCardCreate').for(IndexCard).onCreated().do(this.updateConcealedFor),
            this.objects.createTrigger('Concealment.OnStickyCreate').for(Sticky).onCreated().do(this.updateConcealedFor),
            this.index.on('updated', this.updateAttributesFor),
            this.index.on('updated', this.updateModeIfRequired),
            () => this.releaseView(),
            () => this.clearAttributes(),
        ];
    }

    private onInfoChange = () => {
        const hi = this.session.get(HandInfo);
        const ci = this.session.get(ConcealmentInfo);
        const enabled = !hi.enabled && ci.enabled;
        if (this.loaded && !enabled) {
            this.loaded = false;
            this.unload();
        }
        if (enabled && !this.loaded) {
            this.loaded = true;
            const bs = this.load();
            this.unload = () => Burden.resolveAll(bs);
        }
    };

    private updateConcealedFor = (elmts: Iterable<Component>) => {
        const { mode, mostly } = this.session.get(ConcealmentInfo);
        for (const e of elmts) {
            if (!e.alive) continue;
            const ce = e.as(Concealable);
            if (!ce || ce.userId !== this.myUserId) continue;
            ce.concealed = !!mostly || mode === 'private';
        }
        this.updateAttributesFor(elmts);
    };

    private updateAttributesFor = (elmts: Iterable<Component>) => {
        for (const e of elmts) {
            if (!e.alive) continue;
            const ce = e.as(Concealable);
            if (!ce) continue;
            IsActuallyConcealed.setMaybe(ce, ce.userId !== this.myUserId && ce.concealed);
            IsConcealedHintVisible.setMaybe(ce, ce.userId === this.myUserId);
        }
    };

    private updateAttributes = () => this.updateAttributesFor(this.objects.getAll(Concealable));

    private clearAttributes() {
        const elmts = this.objects.getAll(Concealable);
        for (const e of elmts) {
            IsActuallyConcealed.set(e, null);
            IsConcealedHintVisible.set(e, null);
        }
    }

    private updateModeIfRequired = () => {
        const { mode, mostly } = this.determineMode();
        this.session.patch(ConcealmentInfo, { mode, mostly });
    };

    private releaseView() {
        const { widgets } = this;
        const cwc = widgets.get('concealing');
        if (!cwc) return;
        widgets.remove(cwc);
    }

    private determineMode() {
        const pri = this.index.count(this.myUserId, true);
        const pub = this.index.count(this.myUserId, false);
        if (pub + pri === 0) {
            const { mode } = this.session.get(ConcealmentInfo);
            return { mode, mostly: false };
        }
        else {
            const mode = (pub > pri ? 'public' : 'private') as 'public' | 'private';
            const mostly = pri !== 0 && pub !== 0 && (pri + pub) !== 0;
            return { mode, mostly };
        }
    }
}
